import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePipe, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { DeleteIconComponent } from '@itero/ui-toolkit-angular/icons';
import { RxTextareaCancelDirective, RxTextareaComponent, RxTextareaSubmitDirective } from '../rx-textarea';
import { IntuitiveNote } from '../../models/intuitive-note.model';
import {
	notesViewportFocusUtil,
	RX_TEXTAREA_INPUT_SELECTOR
} from '@shared/utils/notes-viewport-focus.util';

@Component({
	selector: 'rx-note-card',
	templateUrl: './note-card.component.html',
	styleUrls: ['./note-card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		DatePipe,
		NgIf,
		NgSwitch,
		NgSwitchCase,
		NgSwitchDefault,
		ReactiveFormsModule,
		TranslateModule,
		TlkCardComponent,
		TlkButtonComponent,
		DeleteIconComponent,
		RxTextareaComponent,
		RxTextareaSubmitDirective,
		RxTextareaCancelDirective
	]
})
export class NoteCardComponent implements AfterViewChecked, OnInit, OnChanges, OnDestroy {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly hostElement = inject(ElementRef<HTMLElement>);
	private readonly destroy$ = new Subject<void>();

	/** Enter edit → true; cleared after scroll/focus or if no textarea. */
	private pendingScrollAndFocusAfterEditOpen = false;

	readonly editControl = new FormControl<string>('', { nonNullable: false });

	@Input() note: IntuitiveNote | null = null;
	@Input() dateFormat: string | undefined;
	@Input() relativeTimeRefreshTick: number | null = null;
	@Input() isEditing = false;
	@Input() canDelete = false;
	@Input() isClickable = false;
	@Input() isPrintContext = false;

	get createdByName(): string | null {
		return this.note?.createdByName ?? null;
	}

	get content(): string | null {
		return this.note?.content ?? null;
	}

	readonly relativeTimeThresholdMinutes = 60;

	get minutesAgo(): number | null {
		if (!this.note?.dateCreated) {
			return null;
		}

		return Math.max(0, Math.floor((Date.now() - this.note.dateCreated.getTime()) / 60_000));
	}

	get showRelativeTime(): boolean {
		return this.minutesAgo !== null && this.minutesAgo <= this.relativeTimeThresholdMinutes;
	}

	get isCardClickable(): boolean {
		return this.isClickable && !this.isPrintContext;
	}

	get cardTabIndex(): number | null {
		if (!this.isCardClickable) {
			return null;
		}
		if (this.isEditing) {
			return null;
		}
		return 0;
	}

	get showEditTextarea(): boolean {
		return this.isEditing && !this.isPrintContext;
	}

	get isSaveDisabled(): boolean {
		return !this.editControl.value?.trim();
	}

	@Output() edit = new EventEmitter<string>();
	@Output() remove = new EventEmitter<void>();
	@Output() editModeChange = new EventEmitter<boolean>();

	ngOnInit(): void {
		this.editControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.cdr.markForCheck();
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.isEditing?.currentValue === true) {
			this.editControl.setValue((this.content ?? '').trimStart());
			if (changes.isEditing.previousValue !== true) {
				this.pendingScrollAndFocusAfterEditOpen = true;
			}
		}
	}

	ngAfterViewChecked(): void {
		if (!this.pendingScrollAndFocusAfterEditOpen) {
			return;
		}
		if (!this.showEditTextarea) {
			this.pendingScrollAndFocusAfterEditOpen = false;
			return;
		}
		this.pendingScrollAndFocusAfterEditOpen = false;
		this.scrollNoteIntoViewThenFocusEditor();
	}

	private scrollNoteIntoViewThenFocusEditor(): void {
		notesViewportFocusUtil.scrollHostNearestThenFocusDescendantTextarea(
			this.hostElement.nativeElement,
			RX_TEXTAREA_INPUT_SELECTOR,
			() => this.cdr.markForCheck()
		);
	}

	onCardClick(event: MouseEvent): void {
		if ((event.target as HTMLElement).closest('.note-action-button')) {
			return;
		}
		if (this.isEditing) {
			return;
		}
		if (!this.isCardClickable) {
			return;
		}
		this.editModeChange.emit(true);
	}

	onCardKeydown(event: KeyboardEvent): void {
		const target = event.target as HTMLElement | null;

		if (target?.tagName === 'TEXTAREA' || target?.tagName === 'INPUT') {
			return;
		}
		if (event.key !== 'Enter' && event.key !== ' ') {
			return;
		}
		if (event.key === ' ') {
			event.preventDefault();
		}
		this.onCardClick(event as unknown as MouseEvent);
	}

	onRemoveClick(event: MouseEvent): void {
		event.stopPropagation();
		this.remove.emit();
	}

	onCancelEdit(): void {
		this.editModeChange.emit(false);
	}

	onSaveEdit(value: string | null): void {
		const trimmed = value?.trim() ?? '';
		const originalNormalized = (this.content ?? '').trim();
		const hasChange = trimmed !== originalNormalized && trimmed.length > 0;

		this.editModeChange.emit(false);
		this.cdr.markForCheck();

		if (hasChange) {
			this.edit.emit(trimmed);
		}
	}
}
